import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { API_URL } from '../services/api';
import { trackEvent } from '../utils/analytics';

interface Vendor {
  id: number;
  slug: string | null;
  name: string;
  city: string;
  state: string;
  specialty: string;
  description: string | null;
  is_featured: boolean;
  is_claimed: boolean;
  is_domain_confirmed?: boolean;
  active_job_count: number;
}

const CITY_META: Record<string, { display: string; state: string; description: string; region?: string }> = {
  // California — LA Metro
  'los-angeles':   { display: 'Los Angeles',   state: 'CA', description: 'Find AV companies, staging crews, lighting and production companies hiring in Los Angeles, CA.', region: 'la-metro' },
  'long-beach':    { display: 'Long Beach',    state: 'CA', description: 'Browse audio visual and live event production companies hiring in Long Beach, CA.', region: 'la-metro' },
  'burbank':       { display: 'Burbank',       state: 'CA', description: 'Find AV rental, staging, and production companies hiring in Burbank, CA — home to major studios and event venues.', region: 'la-metro' },
  'glendale':      { display: 'Glendale',      state: 'CA', description: 'Browse audio visual and event production companies hiring in Glendale, CA.', region: 'la-metro' },
  'pasadena':      { display: 'Pasadena',      state: 'CA', description: 'Find AV and live event companies hiring in Pasadena, CA.', region: 'la-metro' },
  'santa-monica':  { display: 'Santa Monica',  state: 'CA', description: 'Browse AV rental and event production companies hiring in Santa Monica, CA.', region: 'la-metro' },
  // California — Orange County
  'orange-county': { display: 'Orange County', state: 'CA', description: 'Find AV companies, staging crews, and live event production companies hiring across Orange County, CA.', region: 'oc' },
  'irvine':        { display: 'Irvine',        state: 'CA', description: 'Browse audio visual and event production companies hiring in Irvine, CA.', region: 'oc' },
  'anaheim':       { display: 'Anaheim',       state: 'CA', description: 'Find AV rental, staging, and production companies hiring in Anaheim, CA — near the Convention Center and Disneyland.', region: 'oc' },
  'santa-ana':     { display: 'Santa Ana',     state: 'CA', description: 'Browse AV and event production companies hiring in Santa Ana, CA.', region: 'oc' },
  // California — Inland Empire
  'inland-empire': { display: 'Inland Empire', state: 'CA', description: 'Find AV companies, staging crews, and live event production companies hiring across Riverside, Ontario, San Bernardino, and Palm Springs.', region: 'inland-empire' },
  'riverside':     { display: 'Riverside',     state: 'CA', description: 'Browse audio visual and event production companies hiring in Riverside, CA.', region: 'inland-empire' },
  'ontario':       { display: 'Ontario',       state: 'CA', description: 'Find AV rental, staging, and production companies hiring near Ontario, CA and Toyota Arena.', region: 'inland-empire' },
  'palm-springs':  { display: 'Palm Springs',  state: 'CA', description: 'Browse AV and live event production companies serving Palm Springs and the Coachella Valley.', region: 'inland-empire' },
  // Other major markets
  'las-vegas':     { display: 'Las Vegas',   state: 'NV', description: 'Browse audio visual and live event production companies hiring in Las Vegas, NV.' },
  'new-york':      { display: 'New York',    state: 'NY', description: 'Discover AV rental, staging, and event production companies hiring in New York, NY.' },
  'chicago':       { display: 'Chicago',     state: 'IL', description: 'Find audio visual and live event companies hiring in Chicago, IL.' },
  'nashville':     { display: 'Nashville',   state: 'TN', description: 'Browse AV and production companies hiring in Nashville, TN.' },
  'atlanta':       { display: 'Atlanta',     state: 'GA', description: 'Find live event and audio visual production companies hiring in Atlanta, GA.' },
  'houston':       { display: 'Houston',     state: 'TX', description: 'Browse AV and event production companies hiring in Houston, TX.' },
  'dallas':        { display: 'Dallas',      state: 'TX', description: 'Find audio visual and staging companies hiring in Dallas, TX.' },
  'orlando':       { display: 'Orlando',     state: 'FL', description: 'Discover AV rental and event production companies hiring in Orlando, FL.' },
  'phoenix':       { display: 'Phoenix',     state: 'AZ', description: 'Browse audio visual and live event companies hiring in Phoenix, AZ.' },
};

const SPECIALTY_COLORS: Record<string, string> = {
  union_labor: 'bg-blue-100 text-blue-800',
  av_rental: 'bg-purple-100 text-purple-800',
  audio: 'bg-green-100 text-green-800',
  lighting: 'bg-yellow-100 text-yellow-800',
  video: 'bg-red-100 text-red-800',
  staging: 'bg-orange-100 text-orange-800',
  backline: 'bg-pink-100 text-pink-800',
  production: 'bg-indigo-100 text-indigo-800',
  general: 'bg-gray-100 text-gray-700',
};

const SPECIALTY_LABELS: Record<string, string> = {
  union_labor: 'Union Labor', av_rental: 'AV Rental', audio: 'Audio',
  lighting: 'Lighting', video: 'Video', staging: 'Staging',
  backline: 'Backline', production: 'Production', general: 'General',
};

const getVendorPath = (vendor: Pick<Vendor, 'id' | 'slug'>) => `/vendors/${vendor.slug || vendor.id}`;

const getClaimHref = (vendor: Pick<Vendor, 'id' | 'slug'>) => {
  const redirect = `${getVendorPath(vendor)}?claim=1`;
  return `/register?${new URLSearchParams({ role: 'employer', redirect }).toString()}`;
};

export default function CityVendorsPage() {
  const { city } = useParams<{ city: string }>();
  const meta = CITY_META[city || ''];
  const displayCity = meta?.display || (city || '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const metaRegion = meta?.region;

  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!city) return;
    setLoading(true);
    const params = new URLSearchParams({ limit: '60', offset: '0' });
    if (metaRegion) {
      params.set('region', metaRegion);
    } else {
      params.set('city', displayCity);
    }
    fetch(`${API_URL}/api/vendors?${params}`)
      .then(r => r.json())
      .then(data => { setVendors(data.vendors || []); setTotal(data.total || 0); })
      .catch(() => setVendors([]))
      .finally(() => setLoading(false));
  }, [city, displayCity, metaRegion]);

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title={`AV Companies in ${displayCity} | StageCrew`}
        description={meta?.description || `Find audio visual and live event production companies hiring in ${displayCity}.`}
        url={`https://stagecrew.app/vendors/city/${city}`}
        keywords={`AV companies ${displayCity}, audio visual ${displayCity}, event production ${displayCity}, stagehand companies ${displayCity}, lighting companies ${displayCity}`}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: `AV Companies in ${displayCity}`,
          description: `Audio visual and live event production companies in ${displayCity}`,
          url: `https://stagecrew.app/vendors/city/${city}`,
        }}
      />

      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-10">
          <div className="text-sm text-gray-400 mb-2">
            <Link to="/vendors" className="hover:text-indigo-600">Vendor Directory</Link>
            <span className="mx-2">›</span>
            <span>{displayCity}</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            AV & Event Companies in {displayCity}
          </h1>
          <p className="text-gray-500 text-lg">
            {total} companies · audio visual, staging, lighting, production and more
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse">
                <div className="h-5 bg-gray-200 rounded w-3/4 mb-3" />
                <div className="h-4 bg-gray-100 rounded w-1/2 mb-4" />
                <div className="h-3 bg-gray-100 rounded w-full mb-2" />
              </div>
            ))}
          </div>
        ) : vendors.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <p className="text-lg font-medium">No companies found in {displayCity}</p>
            <Link to="/vendors" className="text-indigo-600 text-sm mt-2 inline-block hover:underline">
              Browse all cities →
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {vendors.map(vendor => (
              <article
                key={vendor.id}
                className={`bg-white rounded-xl border p-6 flex flex-col gap-3 hover:shadow-md transition ${
                  vendor.is_featured ? 'border-indigo-300 ring-1 ring-indigo-200' : 'border-gray-200'
                }`}
              >
                <div className="flex flex-wrap gap-2">
                  {vendor.active_job_count > 0 && (
                    <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                      Pay-listed jobs
                    </span>
                  )}
                  {vendor.is_claimed && (
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                      vendor.is_domain_confirmed
                        ? 'text-green-700 bg-green-50'
                        : 'text-gray-600 bg-gray-100'
                    }`}>
                      {vendor.is_domain_confirmed
                        ? 'Business domain confirmed'
                        : 'Managed listing'}
                    </span>
                  )}
                  {!vendor.is_claimed && (
                    <span className="text-xs font-semibold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                      Unclaimed
                    </span>
                  )}
                </div>
                <div>
                  <Link
                    to={getVendorPath(vendor)}
                    className="text-lg font-semibold text-gray-900 hover:text-indigo-600 transition"
                  >
                    {vendor.name}
                  </Link>
                  <p className="text-sm text-gray-500">{vendor.city}, {vendor.state}</p>
                </div>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full self-start ${SPECIALTY_COLORS[vendor.specialty] || SPECIALTY_COLORS.general}`}>
                  {SPECIALTY_LABELS[vendor.specialty] || vendor.specialty}
                </span>
                {vendor.description && (
                  <p className="text-sm text-gray-600 line-clamp-2">{vendor.description}</p>
                )}
                <div className="mt-auto pt-2 flex flex-wrap gap-2">
                  {!vendor.is_claimed && (
                    <Link
                      to={getClaimHref(vendor)}
                      onClick={() => trackEvent('claim_cta_click', 'vendor_claim', 'city_vendor_card')}
                      className="inline-flex items-center justify-center rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 transition"
                    >
                      Claim this page
                    </Link>
                  )}
                  <Link
                    to={getVendorPath(vendor)}
                    className="inline-flex items-center justify-center rounded-lg border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 hover:border-indigo-300 hover:text-indigo-700 transition"
                  >
                    View profile
                  </Link>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* Other cities */}
        <div className="mt-16">
          <h2 className="text-lg font-semibold text-gray-700 mb-4">Browse other cities</h2>
          <div className="flex flex-wrap gap-3">
            {Object.entries(CITY_META)
              .filter(([slug]) => slug !== city)
              .map(([slug, info]) => (
                <Link
                  key={slug}
                  to={`/vendors/city/${slug}`}
                  className="bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-full text-sm hover:border-indigo-400 hover:text-indigo-600 transition"
                >
                  {info.display}
                </Link>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
